import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ─── ARGB Color Palette ────────────────────────────────────────────────────
const C = {
  // Headers / Titles
  headerBg: "FF0F172A",        // slate-900
  headerText: "FFFFFFFF",      // white
  titleBg: "FF1E293B",         // slate-800
  titleText: "FF94A3B8",       // slate-400

  // Positive (income / assets / emerald)
  emeraldBg: "FFD1FAE5",       // emerald-100
  emeraldText: "FF065F46",     // emerald-800
  emeraldAccentBg: "FFECFDF5", // emerald-50 (lighter rows)

  // Negative (expenses / liabilities / rose)
  roseBg: "FFFFE4E6",          // rose-100
  roseText: "FF9F1239",        // rose-800
  roseAccentBg: "FFFFF1F2",   // rose-50

  // Neutral / wants (amber)
  amberBg: "FFFEF3C7",         // amber-100
  amberText: "FF92400E",       // amber-800

  // Row zebra
  rowEven: "FF0F172A",         // slate-900
  rowOdd: "FF162032",          // slightly lighter
  rowTotal: "FF1E293B",        // slate-800

  // Text
  bodyText: "FFE2E8F0",        // slate-200
  mutedText: "FF64748B",       // slate-500
  white: "FFFFFFFF",
};

const GBP_FORMAT = '"£"#,##0.00';
const PCT_FORMAT = '0.00"%"';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function applyStyle(cell, { bold = false, bg, textColor, numFmt, size = 11, align = "left", italic = false } = {}) {
  if (bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.font = {
    name: "Calibri",
    size,
    bold,
    italic,
    color: { argb: textColor || C.bodyText },
  };
  cell.alignment = { vertical: "middle", horizontal: align, wrapText: false };
  if (numFmt) cell.numFmt = numFmt;
}

function addTitleRow(sheet, text, colSpan = 2) {
  const row = sheet.addRow([text]);
  const cell = row.getCell(1);
  applyStyle(cell, { bold: true, size: 12, bg: C.titleBg, textColor: C.titleText });
  if (colSpan > 1) sheet.mergeCells(`A${row.number}:${String.fromCharCode(64 + colSpan)}${row.number}`);
  row.height = 22;
  return row;
}

function addHeaderRow(sheet, headers) {
  const row = sheet.addRow(headers);
  row.eachCell((cell) => {
    applyStyle(cell, { bold: true, size: 11, bg: C.headerBg, textColor: C.headerText });
    cell.border = { bottom: { style: "medium", color: { argb: "FF10B981" } } };
  });
  row.height = 20;
  return row;
}

function addDataRow(sheet, values, options = {}) {
  const { bg, textColor, bold = false, numFmtCols = [], pctCols = [] } = options;
  const row = sheet.addRow(values);
  row.eachCell({ includeEmpty: true }, (cell, colNum) => {
    const nf = pctCols.includes(colNum) ? PCT_FORMAT : numFmtCols.includes(colNum) ? GBP_FORMAT : undefined;
    applyStyle(cell, { bg, textColor, bold, numFmt: nf });
  });
  row.height = 18;
  return row;
}

function addTotalRow(sheet, values, options = {}) {
  const { bg = C.rowTotal, textColor = C.white, numFmtCols = [], pctCols = [] } = options;
  const row = sheet.addRow(values);
  row.eachCell({ includeEmpty: true }, (cell, colNum) => {
    const nf = pctCols.includes(colNum) ? PCT_FORMAT : numFmtCols.includes(colNum) ? GBP_FORMAT : undefined;
    applyStyle(cell, { bg, textColor, bold: true, numFmt: nf });
    cell.border = {
      top: { style: "medium", color: { argb: "FF334155" } },
    };
  });
  row.height = 20;
  return row;
}

function addBlank(sheet, count = 1) {
  for (let i = 0; i < count; i++) {
    const r = sheet.addRow([""]);
    r.height = 8;
    r.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF020617" } };
    });
  }
}

function autoWidth(sheet, minWidth = 12, maxWidth = 45) {
  sheet.columns.forEach((col) => {
    let max = minWidth;
    col.eachCell({ includeEmpty: false }, (cell) => {
      const len = cell.value ? String(cell.value).length + 2 : 0;
      if (len > max) max = len;
    });
    col.width = Math.min(max, maxWidth);
  });
}

function setTabColor(sheet, argb) {
  sheet.properties.tabColor = { argb };
}

// ─── Asset / Liability labels ─────────────────────────────────────────────────
const ASSET_LABELS = {
  isa: "Stocks & Shares ISA",
  pension: "Pension (SIPP / Workplace)",
  house: "Property Value",
  cash: "Cash Savings",
  crypto: "Crypto",
  otherAsset: "Other Assets",
};

const LIABILITY_LABELS = {
  mortgage: "Mortgage",
  studentLoan: "Student Loan",
  creditCard: "Credit Card Debt",
  otherLiability: "Other Liabilities",
};

// ─── TAB 1: Dashboard Summary ────────────────────────────────────────────────
function buildDashboardSheet(wb, data) {
  const {
    monthLabel, totalIncome, totalNeeds, totalWants, totalSavings,
    leftover, savingsRate, netWorth, totalAssets, totalLiabilities,
  } = data;

  const sheet = wb.addWorksheet("Dashboard Summary");
  setTabColor(sheet, "FF10B981"); // emerald

  sheet.columns = [
    { key: "a", width: 32 },
    { key: "b", width: 22 },
    { key: "c", width: 22 },
    { key: "d", width: 18 },
    { key: "e", width: 16 },
  ];

  // ── Workbook title block
  const titleRow = sheet.addRow(["Kara Finance Budget Pro"]);
  const tc = titleRow.getCell(1);
  sheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`);
  applyStyle(tc, { bold: true, size: 16, bg: "FF020617", textColor: "FF10B981" });
  titleRow.height = 32;

  const subRow = sheet.addRow([`Financial Report — ${monthLabel}`]);
  sheet.mergeCells(`A${subRow.number}:E${subRow.number}`);
  applyStyle(subRow.getCell(1), { size: 11, italic: true, bg: "FF020617", textColor: C.mutedText });
  subRow.height = 18;

  addBlank(sheet);

  // ── Section 1: Key Financial Metrics
  addTitleRow(sheet, "KEY FINANCIAL METRICS", 5);
  addHeaderRow(sheet, ["Metric", "Value (GBP)", "Monthly %", "Status", "Notes"]);

  const metrics = [
    {
      label: "Total Net Worth",
      value: netWorth,
      pct: null,
      status: netWorth >= 0 ? "Positive" : "Negative",
      note: "Assets minus Liabilities",
      bg: netWorth >= 0 ? C.emeraldBg : C.roseBg,
      tc: netWorth >= 0 ? C.emeraldText : C.roseText,
    },
    {
      label: "Total Monthly Income",
      value: totalIncome,
      pct: 100,
      status: "—",
      note: "All income sources",
      bg: C.emeraldBg,
      tc: C.emeraldText,
    },
    {
      label: "Total Monthly Expenses",
      value: totalExpenses(data),
      pct: totalIncome > 0 ? (totalExpenses(data) / totalIncome) * 100 : 0,
      status: totalExpenses(data) > totalIncome ? "Over Budget" : "Under Budget",
      note: "Needs + Wants",
      bg: C.roseBg,
      tc: C.roseText,
    },
    {
      label: "Leftover / Cash Flow",
      value: leftover,
      pct: totalIncome > 0 ? (leftover / totalIncome) * 100 : 0,
      status: leftover >= 0 ? "Positive" : "Negative",
      note: "After all outgoings",
      bg: leftover >= 0 ? C.emeraldBg : C.roseBg,
      tc: leftover >= 0 ? C.emeraldText : C.roseText,
    },
    {
      label: "Savings Rate",
      value: totalSavings,
      pct: savingsRate,
      status: savingsRate >= 20 ? "On Track ✓" : "Below 20% Target",
      note: "Target: 20% of income",
      bg: savingsRate >= 20 ? C.emeraldBg : C.amberBg,
      tc: savingsRate >= 20 ? C.emeraldText : C.amberText,
    },
  ];

  metrics.forEach((m) => {
    const row = sheet.addRow([m.label, m.value, m.pct != null ? m.pct / 100 : null, m.status, m.note]);
    row.height = 20;
    applyStyle(row.getCell(1), { bg: m.bg, textColor: m.tc, bold: true });
    applyStyle(row.getCell(2), { bg: m.bg, textColor: m.tc, bold: true, numFmt: GBP_FORMAT, align: "right" });
    applyStyle(row.getCell(3), { bg: m.bg, textColor: m.tc, numFmt: '0.0"%"', align: "right" });
    applyStyle(row.getCell(4), { bg: m.bg, textColor: m.tc });
    applyStyle(row.getCell(5), { bg: m.bg, textColor: C.mutedText, italic: true });
  });

  addBlank(sheet, 2);

  // ── Section 2: 50/30/20 Analysis
  addTitleRow(sheet, "50 / 30 / 20 BUDGET ANALYSIS", 5);
  addHeaderRow(sheet, ["Category", "Your Amount", "% of Income", "Target %", "Status"]);

  const needsPct = totalIncome > 0 ? (totalNeeds / totalIncome) * 100 : 0;
  const wantsPct = totalIncome > 0 ? (totalWants / totalIncome) * 100 : 0;
  const savPct = totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;

  const buckets = [
    { name: "Needs (Essential)", amount: totalNeeds, pct: needsPct, target: 50, bg: needsPct > 50 ? C.roseBg : C.roseAccentBg, tc: C.roseText },
    { name: "Wants (Discretionary)", amount: totalWants, pct: wantsPct, target: 30, bg: C.amberBg, tc: C.amberText },
    { name: "Savings / Investments", amount: totalSavings, pct: savPct, target: 20, bg: C.emeraldBg, tc: C.emeraldText },
  ];

  buckets.forEach((b) => {
    const row = sheet.addRow([b.name, b.amount, b.pct / 100, b.target / 100, b.pct > b.target ? "⚠ Over Target" : "✓ Within Target"]);
    row.height = 20;
    applyStyle(row.getCell(1), { bg: b.bg, textColor: b.tc, bold: true });
    applyStyle(row.getCell(2), { bg: b.bg, textColor: b.tc, numFmt: GBP_FORMAT, align: "right" });
    applyStyle(row.getCell(3), { bg: b.bg, textColor: b.tc, numFmt: '0.0"%"', align: "right" });
    applyStyle(row.getCell(4), { bg: b.bg, textColor: C.mutedText, numFmt: '0"%"', align: "right" });
    applyStyle(row.getCell(5), { bg: b.bg, textColor: b.tc });
  });

  addBlank(sheet, 2);

  // ── Section 3: Quick Balance Sheet
  addTitleRow(sheet, "BALANCE SHEET SNAPSHOT", 5);
  addHeaderRow(sheet, ["Item", "Value", "", "", ""]);
  const bsRows = [
    { label: "Total Assets", value: totalAssets, bg: C.emeraldBg, tc: C.emeraldText },
    { label: "Total Liabilities", value: totalLiabilities, bg: C.roseBg, tc: C.roseText },
    { label: "Net Worth", value: netWorth, bg: netWorth >= 0 ? C.emeraldBg : C.roseBg, tc: netWorth >= 0 ? C.emeraldText : C.roseText },
  ];
  bsRows.forEach((r) => {
    const row = sheet.addRow([r.label, r.value, "", "", ""]);
    row.height = 20;
    applyStyle(row.getCell(1), { bg: r.bg, textColor: r.tc, bold: true });
    applyStyle(row.getCell(2), { bg: r.bg, textColor: r.tc, bold: true, numFmt: GBP_FORMAT, align: "right" });
    [3, 4, 5].forEach((c) => applyStyle(row.getCell(c), { bg: r.bg }));
  });

  autoWidth(sheet);
}

function totalExpenses(data) {
  return data.totalNeeds + data.totalWants;
}

// ─── TAB 2: Budget Planner ───────────────────────────────────────────────────
function buildBudgetSheet(wb, data) {
  const { monthLabel, income, expenses, totalIncome, totalNeeds, totalWants, totalSavings } = data;
  const sheet = wb.addWorksheet("Budget Planner");
  setTabColor(sheet, "FF3B82F6"); // blue

  sheet.columns = [
    { key: "a", width: 30 },
    { key: "b", width: 18 },
    { key: "c", width: 14 },
    { key: "d", width: 16 },
    { key: "e", width: 14 },
  ];

  // Title
  const titleRow = sheet.addRow([`Budget Planner — ${monthLabel}`]);
  sheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`);
  applyStyle(titleRow.getCell(1), { bold: true, size: 14, bg: "FF020617", textColor: "FF3B82F6" });
  titleRow.height = 28;

  addBlank(sheet);

  // ── Income Sources
  addTitleRow(sheet, "INCOME SOURCES", 5);
  addHeaderRow(sheet, ["Source Name", "Monthly Amount", "", "", ""]);

  income.forEach((inc, i) => {
    const row = sheet.addRow([inc.name, inc.amount, "", "", ""]);
    row.height = 18;
    const isEven = i % 2 === 0;
    applyStyle(row.getCell(1), { bg: isEven ? C.rowEven : C.rowOdd, textColor: C.bodyText });
    applyStyle(row.getCell(2), { bg: isEven ? C.rowEven : C.rowOdd, textColor: C.emeraldText, numFmt: GBP_FORMAT, align: "right" });
    [3, 4, 5].forEach((c) => applyStyle(row.getCell(c), { bg: isEven ? C.rowEven : C.rowOdd }));
  });

  // Total income row
  const incTotal = sheet.addRow(["TOTAL INCOME", totalIncome, "", "", ""]);
  incTotal.height = 22;
  applyStyle(incTotal.getCell(1), { bg: C.emeraldBg, textColor: C.emeraldText, bold: true, size: 12 });
  applyStyle(incTotal.getCell(2), { bg: C.emeraldBg, textColor: C.emeraldText, bold: true, size: 12, numFmt: GBP_FORMAT, align: "right" });
  [3, 4, 5].forEach((c) => applyStyle(incTotal.getCell(c), { bg: C.emeraldBg }));
  incTotal.getCell(1).border = { top: { style: "medium", color: { argb: "FF10B981" } } };
  incTotal.getCell(2).border = { top: { style: "medium", color: { argb: "FF10B981" } } };

  addBlank(sheet, 2);

  // ── Bills & Expenses
  addTitleRow(sheet, "BILLS & EXPENSES", 5);
  addHeaderRow(sheet, ["Bill Name", "Monthly Cost", "Due Date", "Category", "Vital?"]);

  const catColor = {
    needs: { bg: C.roseAccentBg, tc: C.roseText },
    wants: { bg: C.amberBg, tc: C.amberText },
    savings: { bg: C.emeraldBg, tc: C.emeraldText },
  };

  expenses.forEach((exp, i) => {
    const color = catColor[exp.category] || { bg: C.rowEven, tc: C.bodyText };
    const isEven = i % 2 === 0;
    const rowBg = isEven ? C.rowEven : C.rowOdd;
    const row = sheet.addRow([
      exp.name,
      exp.amount,
      exp.dueDate ? `${exp.dueDate}th` : "—",
      exp.category.charAt(0).toUpperCase() + exp.category.slice(1),
      exp.vital ? "Vital" : "Non-Vital",
    ]);
    row.height = 18;
    applyStyle(row.getCell(1), { bg: rowBg, textColor: C.bodyText });
    applyStyle(row.getCell(2), { bg: color.bg, textColor: color.tc, numFmt: GBP_FORMAT, align: "right" });
    applyStyle(row.getCell(3), { bg: rowBg, textColor: C.mutedText, align: "center" });
    applyStyle(row.getCell(4), { bg: color.bg, textColor: color.tc, bold: true, align: "center" });
    applyStyle(row.getCell(5), {
      bg: exp.vital ? "FFE0F2FE" : "FFF8FAFC",
      textColor: exp.vital ? "FF0369A1" : C.mutedText,
      align: "center",
    });
  });

  addBlank(sheet);

  // Category subtotals
  const subtotals = [
    { label: "Needs Subtotal", value: totalNeeds, bg: C.roseAccentBg, tc: C.roseText },
    { label: "Wants Subtotal", value: totalWants, bg: C.amberBg, tc: C.amberText },
    { label: "Savings Subtotal", value: totalSavings, bg: C.emeraldBg, tc: C.emeraldText },
  ];
  subtotals.forEach((s) => {
    const r = sheet.addRow([s.label, s.value, "", "", ""]);
    r.height = 18;
    applyStyle(r.getCell(1), { bg: s.bg, textColor: s.tc, bold: true });
    applyStyle(r.getCell(2), { bg: s.bg, textColor: s.tc, bold: true, numFmt: GBP_FORMAT, align: "right" });
    [3, 4, 5].forEach((c) => applyStyle(r.getCell(c), { bg: s.bg }));
  });

  const totalExpRow = sheet.addRow(["TOTAL EXPENSES + SAVINGS", totalNeeds + totalWants + totalSavings, "", "", ""]);
  totalExpRow.height = 22;
  applyStyle(totalExpRow.getCell(1), { bg: C.roseBg, textColor: C.roseText, bold: true, size: 12 });
  applyStyle(totalExpRow.getCell(2), { bg: C.roseBg, textColor: C.roseText, bold: true, size: 12, numFmt: GBP_FORMAT, align: "right" });
  [3, 4, 5].forEach((c) => applyStyle(totalExpRow.getCell(c), { bg: C.roseBg }));
  totalExpRow.getCell(1).border = { top: { style: "medium", color: { argb: "FFF43F5E" } } };
  totalExpRow.getCell(2).border = { top: { style: "medium", color: { argb: "FFF43F5E" } } };

  autoWidth(sheet);
}

// ─── TAB 3: Net Worth Breakdown ──────────────────────────────────────────────
function buildNetWorthSheet(wb, data) {
  const { monthLabel, assets, liabilities, totalAssets, totalLiabilities, netWorth } = data;
  const sheet = wb.addWorksheet("Net Worth Breakdown");
  setTabColor(sheet, netWorth >= 0 ? "FF10B981" : "FFF43F5E");

  sheet.columns = [
    { key: "a", width: 32 },
    { key: "b", width: 22 },
    { key: "c", width: 10 },
  ];

  // Title
  const titleRow = sheet.addRow([`Net Worth Breakdown — ${monthLabel}`]);
  sheet.mergeCells(`A${titleRow.number}:C${titleRow.number}`);
  applyStyle(titleRow.getCell(1), { bold: true, size: 14, bg: "FF020617", textColor: "FF10B981" });
  titleRow.height = 28;

  addBlank(sheet);

  // ── Assets
  addTitleRow(sheet, "ASSETS", 3);
  addHeaderRow(sheet, ["Asset", "Value", ""]);

  const assetEntries = Object.entries(ASSET_LABELS).map(([key, label]) => ({
    label,
    value: Number(assets[key]) || 0,
  }));

  assetEntries.forEach((a, i) => {
    const hasValue = a.value > 0;
    const isEven = i % 2 === 0;
    const bg = hasValue ? C.emeraldAccentBg : isEven ? C.rowEven : C.rowOdd;
    const tc = hasValue ? C.emeraldText : C.mutedText;
    const row = sheet.addRow([a.label, a.value, ""]);
    row.height = 19;
    applyStyle(row.getCell(1), { bg, textColor: tc, bold: hasValue });
    applyStyle(row.getCell(2), { bg, textColor: tc, bold: hasValue, numFmt: GBP_FORMAT, align: "right" });
    applyStyle(row.getCell(3), { bg });
  });

  const totAssetsRow = sheet.addRow(["TOTAL ASSETS", totalAssets, ""]);
  totAssetsRow.height = 22;
  applyStyle(totAssetsRow.getCell(1), { bg: C.emeraldBg, textColor: C.emeraldText, bold: true, size: 12 });
  applyStyle(totAssetsRow.getCell(2), { bg: C.emeraldBg, textColor: C.emeraldText, bold: true, size: 12, numFmt: GBP_FORMAT, align: "right" });
  applyStyle(totAssetsRow.getCell(3), { bg: C.emeraldBg });
  totAssetsRow.getCell(1).border = { top: { style: "medium", color: { argb: "FF10B981" } } };
  totAssetsRow.getCell(2).border = { top: { style: "medium", color: { argb: "FF10B981" } } };

  addBlank(sheet, 2);

  // ── Liabilities
  addTitleRow(sheet, "LIABILITIES", 3);
  addHeaderRow(sheet, ["Liability", "Value", ""]);

  const liabEntries = Object.entries(LIABILITY_LABELS).map(([key, label]) => ({
    label,
    value: Number(liabilities[key]) || 0,
  }));

  liabEntries.forEach((l, i) => {
    const hasValue = l.value > 0;
    const isEven = i % 2 === 0;
    const bg = hasValue ? C.roseAccentBg : isEven ? C.rowEven : C.rowOdd;
    const tc = hasValue ? C.roseText : C.mutedText;
    const row = sheet.addRow([l.label, l.value, ""]);
    row.height = 19;
    applyStyle(row.getCell(1), { bg, textColor: tc, bold: hasValue });
    applyStyle(row.getCell(2), { bg, textColor: tc, bold: hasValue, numFmt: GBP_FORMAT, align: "right" });
    applyStyle(row.getCell(3), { bg });
  });

  const totLiabRow = sheet.addRow(["TOTAL LIABILITIES", totalLiabilities, ""]);
  totLiabRow.height = 22;
  applyStyle(totLiabRow.getCell(1), { bg: C.roseBg, textColor: C.roseText, bold: true, size: 12 });
  applyStyle(totLiabRow.getCell(2), { bg: C.roseBg, textColor: C.roseText, bold: true, size: 12, numFmt: GBP_FORMAT, align: "right" });
  applyStyle(totLiabRow.getCell(3), { bg: C.roseBg });
  totLiabRow.getCell(1).border = { top: { style: "medium", color: { argb: "FFF43F5E" } } };
  totLiabRow.getCell(2).border = { top: { style: "medium", color: { argb: "FFF43F5E" } } };

  addBlank(sheet, 2);

  // ── Net Worth Final Row
  const nwBg = netWorth >= 0 ? C.emeraldBg : C.roseBg;
  const nwTc = netWorth >= 0 ? C.emeraldText : C.roseText;
  const nwRow = sheet.addRow(["NET WORTH (Assets − Liabilities)", netWorth, ""]);
  nwRow.height = 28;
  sheet.mergeCells(`A${nwRow.number}:A${nwRow.number}`);
  applyStyle(nwRow.getCell(1), { bg: nwBg, textColor: nwTc, bold: true, size: 13 });
  applyStyle(nwRow.getCell(2), { bg: nwBg, textColor: nwTc, bold: true, size: 13, numFmt: GBP_FORMAT, align: "right" });
  applyStyle(nwRow.getCell(3), { bg: nwBg });
  nwRow.getCell(1).border = {
    top: { style: "thick", color: { argb: netWorth >= 0 ? "FF10B981" : "FFF43F5E" } },
    bottom: { style: "thick", color: { argb: netWorth >= 0 ? "FF10B981" : "FFF43F5E" } },
  };
  nwRow.getCell(2).border = {
    top: { style: "thick", color: { argb: netWorth >= 0 ? "FF10B981" : "FFF43F5E" } },
    bottom: { style: "thick", color: { argb: netWorth >= 0 ? "FF10B981" : "FFF43F5E" } },
  };

  addBlank(sheet, 2);

  // ── Footer note
  const noteRow = sheet.addRow(["Generated by Kara Finance Budget Pro. For informational purposes only — not financial advice.", "", ""]);
  sheet.mergeCells(`A${noteRow.number}:C${noteRow.number}`);
  applyStyle(noteRow.getCell(1), { italic: true, textColor: C.mutedText, bg: "FF020617", size: 9 });
  noteRow.height = 14;

  autoWidth(sheet);
}

// ─── Main export function ────────────────────────────────────────────────────
export async function exportFinanceData(appState, derivedValues) {
  const {
    totalIncome, totalNeeds, totalWants, totalSavings,
    leftover, savingsRate, netWorth, totalAssets, totalLiabilities,
  } = derivedValues;

  const { currentMonth, months, assets, liabilities } = appState;
  const monthData = months[currentMonth] || { income: [], expenses: [] };

  const [year, month] = currentMonth.split("-");
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const monthLabel = `${MONTHS[parseInt(month, 10) - 1]} ${year}`;

  const data = {
    monthLabel,
    totalIncome,
    totalNeeds,
    totalWants,
    totalSavings,
    leftover,
    savingsRate,
    netWorth,
    totalAssets,
    totalLiabilities,
    income: monthData.income,
    expenses: monthData.expenses,
    assets,
    liabilities,
  };

  const wb = new ExcelJS.Workbook();
  wb.creator = "Kara Finance Budget Pro";
  wb.lastModifiedBy = "Kara Finance Budget Pro";
  wb.created = new Date();
  wb.modified = new Date();
  wb.properties.date1904 = false;

  buildDashboardSheet(wb, data);
  buildBudgetSheet(wb, data);
  buildNetWorthSheet(wb, data);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileName = `Kara Finance_${year}-${month}.xlsx`;
  saveAs(blob, fileName);
}
